<template>
  <section class="home-tab" :style="homeLayoutStyle">
    <header ref="homeHeaderRef" class="home-header">
      <button
        class="account-chip"
        type="button"
        @click="showAccountSheet = true"
      >
        <div class="account-chip__avatar">
          <app-image
            v-if="avatarUrl"
            class="account-chip__avatar-image"
            :src="avatarUrl"
            alt=""
          />
          <span v-else>{{ avatarText }}</span>
        </div>
        <div class="account-chip__meta">
          <div class="name">
            {{ accountName }}
            <i class="current-card-bar__caret"></i>
          </div>
          <span>{{ shortAddress(profileStore.currentAddress) || "--" }}</span>
        </div>
      </button>

      <div class="home-header__tools">
        <button
          class="node-chip"
          type="button"
          @click="router.push('/home/node')"
        >
          <span :class="['node-chip__dot', nodeStatusClass]" />
        </button>
        <button
          class="language-chip"
          type="button"
          @click="router.push('/mine/language')"
        >
          <app-image :src="languageIcon" alt="" />
        </button>
        <button
          class="network-chip"
          type="button"
          @click="router.push('/home/network')"
        >
          <app-image :src="currentNetworkIcon" alt="" />
          <app-image src="@/assets/images/homeImage/switch_arrows.png" alt="" />
        </button>
      </div>
    </header>

    <div class="home-body">
      <van-pull-refresh
        v-model="refreshing"
        class="home-refresh"
        :head-height="46"
        @refresh="handleRefresh"
      >
        <template #pulling="{ distance }">
          <CustomPullRefreshHead :distance="distance" />
        </template>
        <template #loosing="{ distance }">
          <CustomPullRefreshHead :distance="distance" />
        </template>
        <template #loading>
          <CustomPullRefreshHead loading :distance="30" />
        </template>
        <div class="home-content">
          <section v-if="showNotifyBar" class="notify-banner">
            <div class="notify-banner__content">
              <app-image class="notify-banner__icon" :src="notifyIcon" alt="" />
              <span>{{ t("home.kycRetryRequired") }}</span>
            </div>
            <button
              type="button"
              class="notify-banner__action"
              @click="openKycNotice"
            >
              {{ t("home.viewNow") }}
            </button>
            <button
              type="button"
              class="notify-banner__close"
              @click="showNotifyBar = false"
            >
              <van-icon name="cross" />
            </button>
          </section>

          <!-- <section v-if="lastTradeShow" class="trade-banner">
            <div class="trade-banner__content">
              <div data-strong>{{ t('home.bill') }}</div>
              <span>{{ t('home.cardTailNo') }} {{ lastTrade.cardNumLastFour || '--' }}</span>
              <span :class="{ fail: Number(lastTrade.status) !== 1 }">
                {{ Number(lastTrade.status) === 1 ? t('home.tradeSuccess') : t('home.tradeFailed') }}
              </span>
              <span>
                {{ Number(lastTrade.amount || 0) < 0 ? '-' : '+' }}${{ formatMoney(Math.abs(Number(lastTrade.amount || 0))) }}
              </span>
              <span>{{ formatTradeTime(lastTrade.createTime) }}</span>
            </div>
            <button type="button" @click="lastTradeShow = false">{{ t('common.close') }}</button>
          </section> -->

          <section class="asset-card" :style="assetCardStyle">
            <div class="asset-card__title" @click="showBalance = !showBalance">
              <span>{{ t("home.totalAssets") }}</span>
              <app-image
                :src="showBalance ? eyeOpenIcon : eyeCloseIcon"
                alt=""
              />
            </div>

            <div class="asset-card__amount">
              <div data-strong>{{ showBalance ? assetTotalText : "***" }}</div>
              <span>USDT</span>
              <!-- <button v-if="showBackupButton" type="button" @click.stop="router.push('/auth/backup-warn')">
                <van-icon name="warning" />
                {{ t('home.backup') }}
              </button> -->
            </div>

            <div class="asset-card__stats">
              <button type="button" @click="router.push('/card/index')">
                <span>{{ t("home.cardAssets") }}></span>
                <div data-strong>
                  {{ showBalance ? formatMoney(cardTotal) : "***" }}
                </div>
              </button>
              <button
                type="button"
                @click="router.push('/wallet/account-assets')"
              >
                <span>{{ t("home.accountAssets") }}></span>
                <div data-strong>
                  {{ showBalance ? formatMoney(accountTotal) : "***" }}
                </div>
              </button>
              <button
                type="button"
                @click="
                  navigateWithState(router, '/card/index', {
                    namespace: 'home-card',
                    payload: { leftArrow: true },
                  })
                "
              >
                <span>{{ t("home.cardWallet") }}></span>
                <div data-strong>{{ showBalance ? cardCount : "***" }}</div>
              </button>
            </div>
          </section>

          <section class="shortcut-row">
            <div
              class="shortcut-row-list"
              v-for="item in shortcuts"
              :key="item.label"
              @click="runShortcut(item)"
            >
              <app-image :src="item.icon" alt="" />
              <span>{{ item.label }}</span>
            </div>
          </section>

          <div class="home-divider" />

          <section
            v-if="cardPaymentBanners.length"
            ref="cardPaymentRef"
            class="card-payment"
            :style="cardPaymentStyle"
          >
            <DesktopSwipe
              ref="cardPaymentSwipeRef"
              class="card-swipe"
              :show-indicators="false"
              :height="cardPaymentHeight || undefined"
              @change="cardPaymentIndex = $event"
            >
              <van-swipe-item
                v-for="(banner, index) in cardPaymentBanners"
                :key="banner.id || banner.image || index"
              >
                <button
                  class="card-swipe__item"
                  type="button"
                  :class="{
                    'card-swipe__item--gap':
                      index < cardPaymentBanners.length - 1,
                  }"
                  @click="openCardPaymentBanner(banner)"
                >
                  <app-image
                    class="card-swipe__image"
                    :src="banner.image"
                    alt=""
                  />
                  <span class="card-swipe__content">
                    <span
                      v-if="banner.title"
                      class="card-swipe__title"
                      :style="{
                        color: resolveBannerColor(banner.titleColor, '#ffffff'),
                      }"
                    >
                      {{ banner.title }}
                    </span>
                    <span
                      v-if="banner.description"
                      class="card-swipe__description"
                      :style="{
                        color: resolveBannerColor(
                          banner.descriptionColor,
                          '#ffffff',
                        ),
                      }"
                    >
                      {{ banner.description }}
                    </span>
                  </span>
                </button>
              </van-swipe-item>
            </DesktopSwipe>
            <div
              v-if="cardPaymentBanners.length > 1"
              class="card-swipe__indicators"
            >
              <span
                v-for="(_, index) in cardPaymentBanners"
                :key="index"
                :class="{ active: cardPaymentIndex === index }"
                class="card-swipe__indicator"
                @click="selectCardPaymentBanner(index)"
              />
            </div>
          </section>

          <section class="asset-panel">
            <div class="asset-panel__tabs">
              <div
                v-for="tab in tabs"
                :key="tab.key"
                :class="{ active: activeTab === tab.key }"
                class="asset-panel-list"
                @click="handleTabChange(tab.key)"
              >
                {{ tab.label }}
              </div>
              <button
                v-if="activeTab !== 'records'"
                class="asset-panel__add"
                type="button"
                :style="{ '--asset-panel-add-icon': `url(${addIcon})` }"
                @click="handlePanelAdd"
              />
            </div>

            <div v-if="activeTab === 'tokens'" class="asset-list">
              <button
                v-for="coin in coins"
                :key="coinKey(coin)"
                class="coin-row"
                type="button"
                @click="openCoinDetail(coin)"
              >
                <app-image :src="coinIcon(coin)" alt="" />
                <div>
                  <div data-strong>{{ coin.symbol }}</div>
                  <span
                    >${{ formatMoney(coin.usdtPrice || coin.price || 0) }}</span
                  >
                </div>
                <div>
                  <div data-strong>{{ formatTokenAmount(coin.balance) }}</div>
                  <span>${{ coinTotal(coin) }}</span>
                </div>
              </button>
              <div v-if="coins.length === 0" class="empty-state">
                <AppEmptyState plain />
                <span>{{ t("home.noData") }}</span>
              </div>
            </div>

            <div v-else-if="activeTab === 'records'" class="asset-list">
              <button
                v-for="record in records"
                :key="record.id || record.hash || record.time"
                class="record-row"
                type="button"
                @click="router.push('/wallet/bill')"
              >
                <div class="record-row__icon">
                  {{ (record.coin || "T").slice(0, 1) }}
                </div>
                <div>
                  <div data-strong>{{ recordTitle(record) }}</div>
                  <span>{{ recordAddress(record) }}</span>
                </div>
                <div>
                  <div data-strong>
                    {{ record.type === "in" ? "+" : "-"
                    }}{{ formatTokenAmount(record.amount) }}
                  </div>
                  <span>{{ record.time || "" }}</span>
                </div>
              </button>
              <div v-if="records.length === 0" class="empty-state">
                <AppEmptyState plain />
                <span>{{ t("home.noData") }}</span>
              </div>
            </div>

            <div v-else class="asset-list">
              <van-swipe-cell
                v-for="nft in nfts"
                :key="nft.contractAddress || nft.name"
                class="nft-swipe-cell"
              >
                <button
                  class="nft-row"
                  type="button"
                  @click="openNftDetail(nft)"
                >
                  <app-image :src="nft.img || emptyIcon" alt="" />
                  <div class="nft-row__meta">
                    <div data-strong>{{ nft.name || nft.symbol || "NFT" }}</div>
                    <span>{{ nft.contractAddress || "--" }}</span>
                  </div>
                  <div data-strong>{{ nft.amount || 0 }}</div>
                </button>

                <!-- <template #right>
                  <van-button
                    square
                    type="danger"
                    class="nft-delete-btn"
                    @click="removeNft(nft)"
                  >
                    {{ t('wallet.removeNft') }}
                  </van-button>
                </template> -->
              </van-swipe-cell>
              <div v-if="nfts.length === 0" class="empty-state">
                <AppEmptyState plain />
                <span>{{ t("home.noData") }}</span>
              </div>
            </div>
          </section>
        </div>
      </van-pull-refresh>
    </div>

    <section v-if="homeBlockedReason" class="home-mask">
      <div class="home-mask__card">
        <p>{{ homeBlockedReason }}</p>
        <van-button round type="primary" @click="handleBlockedAction">
          {{ homeBlockedAction }}
        </van-button>
      </div>
    </section>

    <WalletMoreSheet
      :model-value="showMoreSheet"
      :restore-instantly="restoreMoreSheetInstantly"
      @update:model-value="setShowMoreSheet"
      @preserve="persistMoreSheetState"
      @select="handleMoreSheetSelect"
    />
    <HomeAccountSheet v-model="showAccountSheet" @selected="loadHomeData" />

    <van-popup
      v-model:show="showFirstExplore"
      round
      position="bottom"
      class="first-explore-popup"
    >
      <div class="first-explore-popup__body">
        <app-image
          :src="successPopupIcon"
          alt=""
          class="first-explore-popup__image"
        />
        <h3>{{ firstExploreTitle }}</h3>
        <p>{{ firstExploreSubtitle }}</p>
        <van-button
          block
          round
          type="primary"
          @click="showFirstExplore = false"
        >
          {{ t("auth.startExplore") }}
        </van-button>
      </div>
    </van-popup>
  </section>
</template>

<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from "vue";
import { closeToast, showFailToast, showLoadingToast, showToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  getRouteState,
  navigateWithState,
  saveRouteState,
} from "@/utils/route-state";
import { resolveAvatarText, resolveAvatarUrl } from "@/utils/avatar";
import { useProfileStore } from "@/stores/profile";
import { useWalletTokenStore } from "@/stores/wallet-token";
import { useWalletNftStore } from "@/stores/wallet-nft";
import { indexBanner } from "@/api/common";
import {
  pcCardAssetList,
  pcCardLogLast,
  transactionListV2,
} from "@/api/wallet";
import { cardMyList, pcCardCreatePre } from "@/api/card";
import { getDeviceId } from "@/utils/device";
import {
  formatMoney,
  formatTimelineTime,
  formatTokenAmount,
  shortAddress,
} from "@/utils/format";
import { getRecoverableMnemonic } from "@/utils/walletSession";
import WalletMoreSheet from "@/view/wallet/components/WalletMoreSheet.vue";
import HomeAccountSheet from "@/view/main/components/HomeAccountSheet.vue";
import CustomPullRefreshHead from "@/view/main/components/CustomPullRefreshHead.vue";
import DesktopSwipe from "@/components/DesktopSwipe.vue";
import eyeOpenIcon from "@/assets/images/eye_open.png";
import languageIcon from "@/assets/images/homeImage/changeLanguage_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close.png";
import topupIcon from "@/assets/images/homeImage/topup_icon.png";
import withdrawIcon from "@/assets/images/homeImage/withdraw_icon.png";
import billIcon from "@/assets/images/homeImage/wastebook_icon.png";
import inviteIcon from "@/assets/images/homeImage/inviteFriend_icon.png";
import moreIcon from "@/assets/images/homeImage/morethan_icon.png";
import emptyIcon from "@/assets/images/homeImage/emptyFlag.png";
import assetBgImage from "@/assets/images/homeImage/asset_bg.png";
import networkFallbackIcon from "@/assets/images/homeImage/network_icon.png";
import notifyIcon from "@/assets/images/notify_icon.png";
import usdtIcon from "@/assets/images/usdt.png";
import bnbIcon from "@/assets/images/icon_bnb.png";
import ethIcon from "@/assets/images/icon_ethereum.png";
import addIcon from "@/assets/images/add_icon.png";
import successPopupIcon from "@/assets/images/loginImage/create_success.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const walletTokenStore = useWalletTokenStore();
const walletNftStore = useWalletNftStore();
const refreshing = ref(false);
const showBalance = ref(true);
const activeTab = ref("tokens");
const coins = computed(() => walletTokenStore.assetList);
const records = ref([]);
const nfts = computed(() => walletNftStore.nftList);
const cardTotal = ref(0);
const accountTotal = ref(0);
const cardCount = ref(0);
const showMoreSheet = ref(false);
const showAccountSheet = ref(false);
const lastTrade = ref({});
const lastTradeShow = ref(false);
const showFirstExplore = ref(false);
const showNotifyBar = ref(false);
const kycInfo = ref({});
const silentLoadingConfig = reactive({ meta: { showLoading: true } });
const homeHeaderRef = ref(null);
const homeHeaderOffset = ref(68);
const homeRouteState = computed(() => getRouteState(route, "home-index", {}));
const restoreMoreSheetInstantly = ref(false);
const cardPaymentSwipeRef = ref(null);
const cardPaymentRef = ref(null);
const cardPaymentBanners = ref([]);
const cardPaymentIndex = ref(0);
const cardPaymentHeight = ref(0);
let cardPaymentTimer = 0;
let cardPaymentResizeObserver = null;

const accountName = computed(
  () => profileStore.currentAccount.name || "WinPay",
);
const avatarText = computed(() => resolveAvatarText(accountName.value));
const avatarUrl = computed(() =>
  resolveAvatarUrl(profileStore.currentAccount.avatar),
);
const networkName = computed(
  () =>
    profileStore.currentNetwork.name ||
    profileStore.currentNetwork.symbol ||
    "Network",
);
const currentNetworkIcon = computed(() => {
  if (profileStore.currentNetwork?.img) {
    return profileStore.currentNetwork.img;
  }
  const symbol = String(
    profileStore.currentNetwork?.symbol ||
      profileStore.currentNetwork?.name ||
      "",
  ).toUpperCase();
  if (symbol.includes("BNB")) return bnbIcon;
  if (symbol.includes("ETH")) return ethIcon;
  return networkFallbackIcon;
});
const nodeStatusClass = computed(() => {
  const status = profileStore.currentNetwork.currentNode?.status || 1;
  if (status <= 1) return "node-chip__dot--ok";
  if (status === 2) return "node-chip__dot--warn";
  return "node-chip__dot--bad";
});
const homeBlockedReason = computed(() => {
  if (profileStore.currentNetwork.supportChat === false)
    return t("article.unsupportedNetwork");
  if (profileStore.currentAccount.type === "1")
    return t("article.observerWalletWarn");
  return "";
});
const homeBlockedAction = computed(() =>
  profileStore.currentNetwork.supportChat === false
    ? t("article.switchNetwork")
    : t("article.switchWallet"),
);
const assetTotal = computed(() => cardTotal.value + accountTotal.value);
const assetTotalText = computed(() => formatMoney(assetTotal.value));
const showBackupButton = computed(() =>
  Boolean(getRecoverableMnemonic(profileStore.accountStorageAddress)),
);
const assetCardStyle = computed(() => ({
  backgroundImage: `url(${assetBgImage})`,
}));
const homeLayoutStyle = computed(() => ({
  "--home-header-offset": `${homeHeaderOffset.value}px`,
}));
const cardPaymentStyle = computed(() =>
  cardPaymentHeight.value ? { height: `${cardPaymentHeight.value}px` } : {},
);
const firstExploreTitle = computed(() => {
  const type = profileStore.profile?.setWalletType || "";
  if (type === "createWallet") return t("auth.firstExploreCreateTitle");
  if (type === "createEmail") return t("auth.firstExploreRegisterTitle");
  if (type === "loginEmail") return t("auth.firstExploreLoginTitle");
  if (type === "rememberWords" || type === "privateKey")
    return t("auth.firstExploreImportTitle");
  return t("auth.firstExploreLoginTitle");
});
const firstExploreSubtitle = computed(() => {
  const type = profileStore.profile?.setWalletType || "";
  if (type === "createWallet") return t("auth.firstExploreCreateSubtitle");
  return t("auth.firstExploreDefaultSubtitle");
});

const shortcuts = computed(() => [
  { label: t("home.topup"), icon: topupIcon, to: "/wallet/topup" },
  { label: t("home.withdraw"), icon: withdrawIcon, to: "/wallet/withdraw" },
  { label: t("home.bill"), icon: billIcon, to: "/wallet/bill" },
  { label: t("home.invite"), icon: inviteIcon, to: "/wallet/invite" },
  { label: t("home.more"), icon: moreIcon, action: "more" },
]);
const tabs = computed(() => [
  { key: "tokens", label: t("home.token") },
  { key: "records", label: t("home.turnover") },
  { key: "nfts", label: t("home.nft") },
]);

function resolveBannerColor(value, fallback) {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  if (raw.startsWith("#") || raw.startsWith("rgb")) return raw;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;

  const color = parsed >>> 0;
  const alpha = (color >>> 24) & 0xff;
  const red = (color >>> 16) & 0xff;
  const green = (color >>> 8) & 0xff;
  const blue = color & 0xff;
  return alpha === 0xff
    ? `rgb(${red}, ${green}, ${blue})`
    : `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
}

function selectCardPaymentBanner(index) {
  cardPaymentSwipeRef.value?.swipeTo?.(index);
}

function openCardPaymentBanner(banner) {
  const url = String(banner?.url || "").trim();
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function stopCardPaymentAutoPlay() {
  if (cardPaymentTimer) {
    window.clearInterval(cardPaymentTimer);
    cardPaymentTimer = 0;
  }
}

function startCardPaymentAutoPlay() {
  stopCardPaymentAutoPlay();
  if (cardPaymentBanners.value.length <= 1) return;

  cardPaymentTimer = window.setInterval(() => {
    const nextIndex =
      cardPaymentIndex.value >= cardPaymentBanners.value.length - 1
        ? 0
        : cardPaymentIndex.value + 1;
    cardPaymentSwipeRef.value?.swipeTo?.(nextIndex);
  }, 4000);
}

function updateCardPaymentHeight() {
  const width = cardPaymentRef.value?.getBoundingClientRect?.().width || 0;
  if (!width) return;

  cardPaymentHeight.value = Math.round((width * 300) / 1029);
}

function showTodo() {
  showToast(t("home.actionTodo"));
}

function handlePanelAdd() {
  if (activeTab.value === "tokens") {
    router.push("/wallet/add-token");
    return;
  }
  if (activeTab.value === "nfts") {
    router.push("/wallet/add-nft");
    return;
  }
  showTodo();
}

function handleBlockedAction() {
  if (profileStore.currentNetwork.supportChat === false) {
    router.push("/home/network");
    return;
  }
  showAccountSheet.value = true;
}

function openCoinDetail(coin) {
  walletTokenStore.setCurrentAsset(coin);
  navigateWithState(router, "/wallet/token-detail", {
    namespace: "wallet-token-detail",
    payload: {
      symbol: coin.symbol || coin.coin || "",
      contractAddress: coin.contractAddress || "",
    },
  });
}

function openNftDetail(nft) {
  if (String(profileStore.currentAccount?.type || "") === "1") {
    return;
  }
  walletNftStore.setCurrentNft(nft);
  walletNftStore.setCurrentNftAsset(null);
  router.push("/wallet/nft-detail");
}

function removeNft(nft) {
  walletNftStore.dismissNft(
    nft?.contractAddress,
    profileStore.currentNetwork?.chainNum || 56,
  );
}

async function handleNodeSelected() {
  await loadActiveTabData();
}

function openKycNotice() {
  navigateWithState(router, "/card/kyc/result", {
    namespace: "card-kyc-result",
    payload: {
      holderStatus: String(kycInfo.value?.holderStatus || ""),
      reason: kycInfo.value?.reason || "",
    },
  });
}

function runShortcut(item) {
  if (item.to) {
    router.push(item.to);
    return;
  }
  if (item.action === "more") {
    setShowMoreSheet(true);
    return;
  }
  if (item.action === "open-card") {
    goOpenCard();
    return;
  }
  showTodo();
}

function persistMoreSheetState() {
  const stateKey = saveRouteState("home-index", { showMoreSheet: true });
  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __routeStateKey: stateKey,
      __routeStateNamespace: "home-index",
    },
    "",
  );
}

function setShowMoreSheet(value) {
  showMoreSheet.value = Boolean(value);
  const stateKey = saveRouteState("home-index", {
    showMoreSheet: showMoreSheet.value,
  });
  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __routeStateKey: stateKey,
      __routeStateNamespace: "home-index",
    },
    "",
  );
}

function handleMoreSheetSelect(item) {
  if (item?.action === "open-card") {
    goOpenCard();
  }
}

function coinKey(coin) {
  return coin.contractAddress || coin.coin || coin.symbol || coin.symbolType;
}

function coinIcon(coin) {
  const name = String(coin.coin || coin.symbol || "").toUpperCase();
  if (name.includes("USDT")) return usdtIcon;
  if (name.includes("BNB")) return bnbIcon;
  if (name.includes("ETH")) return ethIcon;
  return coin.img || coin.logo || usdtIcon;
}

function coinTotal(coin) {
  if (
    coin.balanceTo !== undefined &&
    coin.balanceTo !== null &&
    coin.balanceTo !== ""
  ) {
    return formatMoney(Number(coin.balanceTo || 0));
  }
  return formatMoney(
    Number(coin.balance || 0) * Number(coin.usdtPrice || coin.price || 0),
  );
}

function recordDirection(record) {
  const type = String(record?.type || "").toLowerCase();
  if (type === "in" || type === "out") {
    return type;
  }

  const currentAddress = String(
    profileStore.currentAddress || "",
  ).toLowerCase();
  const toAddress = String(record?.toAddress || "").toLowerCase();
  if (currentAddress && toAddress && currentAddress === toAddress) {
    return "in";
  }

  return "out";
}

function recordTitle(record) {
  const direction =
    recordDirection(record) === "in"
      ? t("wallet.transferIn")
      : t("wallet.transferOut");
  return `${direction} ${record.amount || 0} ${record.coin || ""}`;
}

function recordAddress(record) {
  return recordDirection(record) === "in"
    ? shortAddress(record.fromAddress) || "--"
    : shortAddress(record.toAddress) || "--";
}

function formatTradeTime(timestamp) {
  const value = Number(timestamp || 0);
  if (!value) return "";
  return formatTimelineTime(value < 10000000000 ? value * 1000 : value);
}

async function handleRefresh() {
  refreshing.value = true;
  Object.assign(silentLoadingConfig, { meta: { showLoading: false } });
  await loadHomeData();
  refreshing.value = false;
}

function handleTabChange(tabKey) {
  if (activeTab.value === tabKey) {
    return;
  }
  activeTab.value = tabKey;
  loadActiveTabData();
}

async function loadHomeData() {
  const address = profileStore.platformAddress || null;
  await Promise.allSettled([
    loadCardAssets(address),
    loadLastTrade(address),
    loadCardCreatePre(address),
    loadCardPaymentBanners(),
  ]);

  if (activeTab.value !== "tokens") {
    await loadActiveTabData();
  }
  Object.assign(silentLoadingConfig, { meta: { showLoading: false } });
}

async function loadCardPaymentBanners() {
  stopCardPaymentAutoPlay();
  try {
    const result = await indexBanner({ meta: { showLoading: false } });
    cardPaymentBanners.value = Array.isArray(result) ? result : [];
    cardPaymentIndex.value = 0;
    await nextTick();
    updateCardPaymentHeight();
    startCardPaymentAutoPlay();
  } catch (error) {
    cardPaymentBanners.value = [];
    cardPaymentIndex.value = 0;
    cardPaymentHeight.value = 0;
  }
}

async function loadActiveTabData() {
  const address = profileStore.platformAddress || null;
  const chainNum = profileStore.currentNetwork.chainNum || 56;

  if (activeTab.value === "tokens") {
    await loadCardAssets(address);
    return;
  }

  if (activeTab.value === "records") {
    await loadRecords(profileStore.currentAddress || null, chainNum);
    return;
  }

  if (activeTab.value === "nfts") {
    await loadNfts(profileStore.platformAddress || null, chainNum);
  }
}

function withTimeout(task, fallback, timeout = 8000) {
  return Promise.race([
    task,
    new Promise((resolve) => {
      setTimeout(() => resolve(fallback), timeout);
    }),
  ]);
}

async function loadRecords(address, chainNum) {
  if (!address) {
    records.value = [];
    return;
  }
  try {
    const result = await withTimeout(
      transactionListV2(
        {
          address,
          chainNum,
          contractAddress: "0x",
          type: null,
          pageNum: 1,
        },
        silentLoadingConfig,
      ),
      [],
    );
    records.value = Array.isArray(result?.list)
      ? result.list
      : Array.isArray(result)
        ? result
        : [];
  } catch (error) {
    records.value = [];
  }
}

async function loadNfts(address, chainNum) {
  if (!address) {
    walletNftStore.setNftList([], chainNum);
    return;
  }
  try {
    await withTimeout(
      walletNftStore.fetchNftList(address, silentLoadingConfig),
      [],
    );
  } catch (error) {
    walletNftStore.setNftList([], chainNum);
  }
}

async function loadCardAssets(address) {
  const chainNum = profileStore.currentNetwork.chainNum || 56;
  if (!address) {
    cardTotal.value = 0;
    accountTotal.value = 0;
    cardCount.value = 0;
    walletTokenStore.setAssetList([], chainNum);
    profileStore.updateAccountSnapshot({
      assetTotal: 0,
      accountAssetTotal: 0,
      cardAssetTotal: 0,
      cardCount: 0,
      mainBalance: 0,
      mainCoin:
        profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    });
    return;
  }
  try {
    const [assetResult, cardResult] = await Promise.all([
      withTimeout(pcCardAssetList({ address }, silentLoadingConfig), {}),
      withTimeout(
        cardMyList(
          { address, pageNum: 1, pageSize: 999, deviceToken: getDeviceId() },
          silentLoadingConfig,
        ),
        {},
      ),
    ]);
    const list = Array.isArray(cardResult?.list)
      ? cardResult.list
      : Array.isArray(cardResult)
        ? cardResult
        : [];
    cardCount.value = list.length;
    cardTotal.value = list.reduce(
      (sum, item) => sum + Number(item?.balance?.available || 0),
      0,
    );
    accountTotal.value = Number(assetResult?.totalTo || 0);
    const assetList = Array.isArray(assetResult?.list) ? assetResult.list : [];
    walletTokenStore.setAssetList(assetList, chainNum);
    const mainCoin = String(
      profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    )
      .trim()
      .toUpperCase();
    const mainAsset =
      assetList.find(
        (item) =>
          String(item?.symbol || item?.coin || "")
            .trim()
            .toUpperCase() === mainCoin,
      ) || null;
    profileStore.updateAccountSnapshot({
      assetTotal: cardTotal.value + accountTotal.value,
      accountAssetTotal: accountTotal.value,
      cardAssetTotal: cardTotal.value,
      cardCount: cardCount.value,
      mainBalance: mainAsset?.balanceNormal ?? mainAsset?.balance ?? 0,
      mainCoin:
        mainCoin ||
        profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    });
  } catch (error) {
    cardTotal.value = 0;
    accountTotal.value = 0;
    cardCount.value = 0;
    walletTokenStore.setAssetList(
      [],
      profileStore.currentNetwork.chainNum || 56,
    );
    profileStore.updateAccountSnapshot({
      assetTotal: 0,
      accountAssetTotal: 0,
      cardAssetTotal: 0,
      cardCount: 0,
      mainBalance: 0,
      mainCoin:
        profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    });
  }
}

async function loadLastTrade(address) {
  if (!address) {
    lastTrade.value = {};
    lastTradeShow.value = false;
    return;
  }
  try {
    const result = await withTimeout(
      pcCardLogLast({ address }, silentLoadingConfig),
      {},
    );
    lastTrade.value = result && typeof result === "object" ? result : {};
    lastTradeShow.value = !!Object.keys(lastTrade.value).length;
  } catch (error) {
    lastTrade.value = {};
    lastTradeShow.value = false;
  }
}

async function loadCardCreatePre(address) {
  if (!address) {
    kycInfo.value = {};
    showNotifyBar.value = false;
    return;
  }

  try {
    const result = await pcCardCreatePre({ address }, silentLoadingConfig);
    kycInfo.value = result && typeof result === "object" ? result : {};
    showNotifyBar.value = !!String(kycInfo.value?.reason || "").trim();
  } catch (error) {
    kycInfo.value = {};
    showNotifyBar.value = false;
  }
}

async function goOpenCard() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = await pcCardCreatePre({
      address: profileStore.platformAddress,
    });
    const holderStatus = Number(result?.holderStatus || 0);
    if (holderStatus === 3) {
      navigateWithState(router, "/wallet/open-card", {
        namespace: "card-open",
        payload: { type: 2 },
      });
      return;
    }
    if (
      holderStatus === 2 ||
      holderStatus === 4 ||
      (holderStatus === 1 && String(result?.reason || "").trim())
    ) {
      navigateWithState(router, "/card/kyc/result", {
        namespace: "card-kyc-result",
        payload: {
          holderStatus: String(holderStatus),
          reason: result?.reason || "",
        },
      });
      return;
    }
    router.push("/card/kyc");
  } catch (error) {
    showFailToast(error.message || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

function openFirstExplorePopup() {
  if (!profileStore.profile?.isFirst) {
    return;
  }
  showFirstExplore.value = true;
  profileStore.setFirstVisit(false);
}

function updateHomeHeaderOffset() {
  const headerHeight = Math.ceil(
    homeHeaderRef.value?.getBoundingClientRect?.().height || 0,
  );
  if (headerHeight > 0) {
    homeHeaderOffset.value = headerHeight + 8;
  }
}

function syncHomeViewportLayout() {
  updateHomeHeaderOffset();
  updateCardPaymentHeight();
}

function resetHomeScroll() {
  const activeEl = document.activeElement;
  if (
    activeEl &&
    typeof activeEl.blur === "function" &&
    (activeEl.tagName === "INPUT" ||
      activeEl.tagName === "TEXTAREA" ||
      activeEl.tagName === "SELECT" ||
      activeEl.isContentEditable)
  ) {
    activeEl.blur();
  }

  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  const refreshEl = document.querySelector(".home-refresh");
  const scrollTarget =
    refreshEl?.querySelector?.(".van-pull-refresh") || refreshEl;
  scrollTarget?.scrollTo?.({ top: 0, behavior: "auto" });
}

onMounted(async () => {
  const shouldRestoreMoreSheet = Boolean(homeRouteState.value?.showMoreSheet);
  restoreMoreSheetInstantly.value = shouldRestoreMoreSheet;
  showMoreSheet.value = shouldRestoreMoreSheet;
  await loadHomeData();
  await nextTick();
  if (shouldRestoreMoreSheet) {
    requestAnimationFrame(() => {
      restoreMoreSheetInstantly.value = false;
    });
  }
  syncHomeViewportLayout();
  if (window.ResizeObserver) {
    cardPaymentResizeObserver = new ResizeObserver(() => {
      updateCardPaymentHeight();
    });
    if (cardPaymentRef.value) {
      cardPaymentResizeObserver.observe(cardPaymentRef.value);
    }
  }
  requestAnimationFrame(() => {
    syncHomeViewportLayout();
    resetHomeScroll();
  });
  window.setTimeout(() => {
    syncHomeViewportLayout();
    resetHomeScroll();
  }, 120);
  window.setTimeout(() => {
    resetHomeScroll();
  }, 260);
  window.addEventListener("resize", syncHomeViewportLayout, { passive: true });
  window.visualViewport?.addEventListener?.("resize", syncHomeViewportLayout, {
    passive: true,
  });
  window.visualViewport?.addEventListener?.("scroll", syncHomeViewportLayout, {
    passive: true,
  });
  openFirstExplorePopup();
});

onUnmounted(() => {
  stopCardPaymentAutoPlay();
  cardPaymentResizeObserver?.disconnect?.();
  cardPaymentResizeObserver = null;
  window.removeEventListener("resize", syncHomeViewportLayout);
  window.visualViewport?.removeEventListener?.(
    "resize",
    syncHomeViewportLayout,
  );
  window.visualViewport?.removeEventListener?.(
    "scroll",
    syncHomeViewportLayout,
  );
});
</script>

<style scoped lang="scss">
.home-tab {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  min-width: 0;
  padding: 0 14px 0;
  overflow: hidden;
  background: #ffffff;
}

.home-body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  margin-top: var(
    --home-header-offset,
    calc(60px + env(safe-area-inset-top) + 8px)
  );
  overflow: hidden;
}

.home-refresh {
  height: 100%;
  min-width: 0;
  overflow: auto;
}

.home-content {
  min-width: 0;
  padding-top: 2px;
  padding-bottom: calc(68px + env(safe-area-inset-bottom) + 16px);
}

.first-explore-popup {
  padding: 16px 16px calc(20px + env(safe-area-inset-bottom));
}

.first-explore-popup__body {
  text-align: center;

  h3 {
    margin: 8px 0 10px;
    color: #000000;
    font-size: 20px;
    font-weight: 700;
  }

  p {
    margin: 0 0 24px;
    color: #666666;
    font-size: 14px;
    line-height: 22px;
  }
}

.first-explore-popup__image {
  display: block;
  width: 125px;
  margin: 0 auto;
  object-fit: contain;
}

.trade-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #eef5ff;

  button {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: var(--wp-color-sub-text);
    font-size: 12px;
  }
}

.fw {
  font-weight: bold;
}

.notify-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  margin-top: 2px;
  margin-bottom: 10px;
  padding: 5px 8px 5px 10px;
  border-radius: 8px;
  background: #eef5ff;
}

.notify-banner__content {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 2px;
  color: #2c6bff;
  font-size: 13px;
  line-height: 18px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.notify-banner__icon {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  object-fit: contain;
}

.notify-banner__action,
.notify-banner__close {
  border: 0;
  background: transparent;
}

.notify-banner__action {
  flex: 0 0 auto;
  height: 24px;
  padding: 0 10px;
  border: 1px solid #2c6bff;
  border-radius: 999px;
  color: #2c6bff;
  font-size: 12px;
  line-height: 22px;
}

.notify-banner__close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: #999999;
  font-size: 14px;
}

.trade-banner__content {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 4px 8px;
  color: var(--wp-color-theme);
  font-size: 12px;

  [data-strong] {
    color: var(--wp-color-text);
    font-size: 13px;
  }

  .fail {
    color: #f53f3f;
  }
}

.home-header {
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: calc(60px + env(safe-area-inset-top));
  padding-top: env(safe-area-inset-top);
  box-sizing: border-box;
  background: #fff;
  position: fixed;
  left: 14px;
  top: 0;
  right: 14px;
}

.account-chip,
.node-chip,
.language-chip,
.network-chip {
  border: 0;
  background: transparent;
}

.account-chip {
  display: flex;
  min-width: 0;
  align-items: center;
  padding: 0;
  color: var(--wp-color-text);
}

.account-chip__avatar {
  display: flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4080ff, #14c9c9);
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
}

.account-chip__avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.account-chip__meta {
  min-width: 0;
  margin-left: 7px;
  text-align: left;

  .name,
  span {
    display: block;
    overflow: hidden;
    max-width: 118px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name {
    font-size: 13px;
    font-weight: 500;

    i {
      display: inline-block;
      margin-left: 2px;
    }
  }

  span {
    margin-top: 2px;
    color: var(--wp-color-sub-text);
    font-size: 12px;
  }

  .current-card-bar__caret {
    width: 0;
    height: 0;
    border-top: 6px solid #000;
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
  }
}

.home-header__tools {
  display: flex;
  align-items: center;
  gap: 5px;
}

.node-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #c9cdd4;
  border-radius: 10px;
}

.node-chip__dot {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(0, 180, 42, 0.3);
  border-radius: 50%;
  background: #00b42a;
}

.node-chip__dot--warn {
  border-color: rgba(250, 140, 21, 0.3);
  background: #fa8c15;
}

.node-chip__dot--bad {
  border-color: rgba(245, 63, 63, 0.3);
  background: #f53f3f;
}

.language-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;

  .app-image {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
}

.network-chip {
  display: flex;
  align-items: center;
  max-width: 148px;
  height: 34px;
  padding: 5px 8px;
  border: 1px solid #e5e5e5;
  border-radius: 50px;
  color: var(--wp-color-text);
  font-size: 12px;

  .app-image {
    width: 22px;
    height: 22px;
  }

  span {
    overflow: hidden;
    max-width: 78px;
    margin: 0 5px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .app-image:last-child {
    width: 16px;
    height: 16px;
    margin-left: 5px;
  }
}

.asset-card {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 23px 16px;
  border-radius: 16px;
  background-color: #2f58f7;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(22, 93, 255, 0.18);

  [data-strong] {
    color: #fff;
  }
}

.home-mask {
  position: absolute;
  inset: env(safe-area-inset-top) 0 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7);
}

.home-mask__card {
  width: 100%;
  max-width: 320px;
  text-align: center;

  p {
    margin: 0 0 20px;
    color: var(--wp-color-text);
    font-size: 14px;
    line-height: 22px;
  }
}

.asset-card__title {
  display: flex;
  align-items: center;
  // justify-content: space-between;
  font-size: 16px;

  span {
    font-weight: bold;
  }

  .app-image {
    width: 18px;
    height: 18px;
    filter: brightness(0) invert(1);
  }
}

.asset-card__amount {
  display: flex;
  min-width: 0;
  align-items: flex-end;
  margin-top: 13px;

  [data-strong] {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 24px;
    font-weight: 700;
  }

  span {
    flex: 0 0 auto;
    margin-left: 8px;
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 3px;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 30px;
    margin-left: auto;
    padding: 0 10px;
    border: 1px solid #f2f3f5;
    border-radius: 50px;
    background: #f7f8fa;
    color: #000000;
    font-size: 14px;
  }
}

.asset-card__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  min-width: 0;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);

  button {
    min-width: 0;
    min-width: 0;
    padding: 0 12px;
    border: 0;
    border-right: 1px solid rgba(255, 255, 255, 0.15);
    background: transparent;
    color: #ffffff;
    text-align: left;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border-right: 0;
      padding-right: 0;
    }
  }

  span,
  [data-strong] {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 12px;
    font-weight: bold;
  }

  [data-strong] {
    margin-top: 5px;
    font-size: 13px;
    font-weight: 700;
  }
}

.shortcut-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 20px;
  text-align: center;

  &-list {
    border: 0;
    background: transparent;
    color: var(--wp-color-text);
    font-size: 12px;
  }

  .app-image {
    width: 48px;
    height: 48px;
    margin: 0 auto 10px;
  }
}

.home-divider {
  width: 100%;
  height: 1px;
  margin: 20px 0;
  background: #f4f5f6;
}

.card-payment {
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  aspect-ratio: 1029 / 300;
  overflow: hidden;
  contain: layout paint;

  .card-swipe {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  :deep(.desktop-swipe),
  .card-swipe,
  :deep(.van-swipe) {
    width: 100%;
    height: 100%;
  }

  :deep(.van-swipe__track),
  :deep(.van-swipe-item) {
    height: 100%;
    min-width: 0;
  }

  .card-swipe__item {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 0 16px;
    border: 0;
    background: transparent;
    border-radius: 12px;
    text-align: left;
    line-height: normal;
  }

  .card-swipe__item--gap {
    width: calc(100% - 5px);
  }

  .card-swipe__image {
    position: absolute;
    inset: 0;
    z-index: 0;
    display: block;
    width: 100%;
    height: 100%;
    max-width: none;
    border-radius: inherit;
  }

  .card-swipe__image :deep(.van-image__img) {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: fill;
  }

  .card-swipe__content {
    position: absolute;
    z-index: 1;
    display: flex;
    left: 16px;
    bottom: 0;
    height: 100%;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .card-swipe__title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.35;
  }

  .card-swipe__description {
    font-size: 12px;
    line-height: 1.35;
  }

  .card-swipe__indicators {
    position: absolute;
    right: 0;
    bottom: 10px;
    left: 0;
    z-index: 2;
    display: flex;
    justify-content: center;
    pointer-events: auto;
  }

  .card-swipe__indicator {
    width: 8px;
    height: 8px;
    margin: 0 4px;
    border-radius: 20px;
    background: #ffffff;
  }

  .card-swipe__indicator.active {
    background: var(--wp-color-theme);
  }
}

.asset-panel {
  margin-top: 20px;
  padding-bottom: 10px;
}

.asset-panel__tabs {
  display: flex;
  align-items: center;

  .asset-panel-list {
    margin-right: 10px;
    position: relative;
    padding: 0 0 10px;
    border: 0;
    background: transparent;
    color: var(--wp-color-sub-text);
    font-size: 18px;
  }

  .active {
    color: #000;
    font-weight: 700;
  }

  .active::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 3px;
    margin: auto;
    border-radius: 30px;
    background: #3135ac;
    box-shadow: 0 2px 4px rgba(49, 53, 172, 0.4);
    content: "";
  }

  .asset-panel__add {
    margin-left: auto;
    width: 25px;
    height: 25px;
    padding: 0;
    border: 0;
    background-color: #000000;
    mask: var(--asset-panel-add-icon) center / contain no-repeat;
    -webkit-mask: var(--asset-panel-add-icon) center / contain no-repeat;
  }
}

.asset-list {
  margin-top: 10px;
  background: #ffffff;
}

.nft-swipe-cell {
  background: #ffffff;
  overflow: hidden;
}

.nft-swipe-cell :deep(.van-swipe-cell__wrapper) {
  background: #ffffff;
}

.nft-swipe-cell :deep(.van-swipe-cell__right) {
  display: flex;
  background: #ee0a24;
}

.coin-row,
.record-row,
.nft-row {
  display: flex;
  width: 100%;
  align-items: center;
  padding: 20px 0;
  border: 0;
  border-bottom: 1px solid #f4f5f6;
  background: #ffffff;
  color: var(--wp-color-text);
  text-align: left;

  > .app-image,
  .record-row__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    margin-right: 10px;
    border-radius: 50%;
  }

  > div:nth-child(2) {
    min-width: 0;
    flex: 1;
  }

  > div:last-child {
    text-align: right;
  }

  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    font-size: 16px;
    font-weight: 700;
  }

  span {
    margin-top: 4px;
    color: var(--wp-color-sub-text);
    font-size: 13px;
  }
}

.record-row__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wp-color-theme);
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
}

.nft-row {
  > .app-image {
    border-radius: 8px;
    object-fit: cover;
  }

  > [data-strong] {
    margin-left: 8px;
    flex-shrink: 0;
  }
}

.nft-row__meta {
  min-width: 0;
  flex: 1;
}

.nft-row__meta [data-strong],
.nft-row__meta span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nft-delete-btn {
  width: 72px;
  min-height: 81px;
  border: 0;
  flex-shrink: 0;
}

.nft-delete-btn:deep(.van-button__text) {
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  color: var(--wp-color-sub-text);
  font-size: 12px;

}
</style>
